import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import useInheritedTheme from '../../../Hooks//UseInheritedTheme';
import { CSSTransition } from 'react-transition-group';
import { AnimationTimings } from '../../../Constants/Animation';
import Icon from '../../../Components/Content/Icon';
export var ToastType;
(function (ToastType) {
    ToastType["Success"] = "Success";
    ToastType["Warning"] = "Warning";
    ToastType["Error"] = "Error";
    ToastType["Info"] = "Info";
})(ToastType || (ToastType = {}));
export var ToastDuration;
(function (ToastDuration) {
    ToastDuration[ToastDuration["Short"] = 3000] = "Short";
    ToastDuration[ToastDuration["Medium"] = 6000] = "Medium";
    ToastDuration[ToastDuration["Long"] = 10000] = "Long";
})(ToastDuration || (ToastDuration = {}));
export default function Toast(props) {
    const theme = useInheritedTheme();
    const [isVisible, setIsVisible] = useState(false);
    const [shouldStartCountdown, setShouldStartCountdown] = useState(false);
    const onClose = useCallback(() => {
        setIsVisible(false);
        if (props.onClose) {
            setTimeout(props.onClose, theme.animationDurationMs);
        }
    }, [props.onClose, theme.animationDurationMs]);
    useEffect(() => {
        // Need to do this on the next tick, otherwise the CSS classes won't be correctly applied
        setTimeout(() => setIsVisible(true), 0);
        // Auto close if we want to
        if (props.autoCloseMs && props.autoCloseMs > 0) {
            setTimeout(onClose, props.autoCloseMs + theme.animationDurationMs);
            setTimeout(() => {
                setShouldStartCountdown(true);
            }, theme.animationDurationMs);
        }
    }, [onClose, props.autoCloseMs, theme.animationDurationMs]);
    let action = null;
    if (props.action) {
        const actionCallback = () => {
            props?.action?.callback?.();
            if (props.action?.closeOnClick) {
                onClose();
            }
        };
        action = _jsx(StyledAction, { onClick: actionCallback, children: props.action.text });
    }
    const ref = useRef(null);
    return (_jsx(CSSTransition, { nodeRef: ref, in: isVisible, timeout: theme.animationDurationMs, classNames: "toast", mountOnEnter: true, children: _jsxs(StyledToast, { "$type": props.type, "$autoCloseMs": props.autoCloseMs, ref: ref, theme: theme, children: [_jsx(StyledIcon, { "$type": props.type, "$autoCloseMs": props.autoCloseMs, theme: theme, name: getIconClassNameForToastType(props.type) }), _jsxs(StyledText, { theme: theme, children: [props.text, action] }), _jsx(StyledCloseIcon, { theme: theme, name: "x", onClick: onClose }), _jsx(StyledProgressBar, { "$type": props.type, "$autoCloseMs": props.autoCloseMs, theme: theme, className: shouldStartCountdown ? 'autoclose' : '' })] }) }));
}
const getIconClassNameForToastType = (toastType) => {
    switch (toastType) {
        case ToastType.Error:
            return 'error';
        case ToastType.Warning:
            return 'warning';
        case ToastType.Success:
            return 'check';
        default:
            return 'ai';
    }
};
const getThemeColorForToastType = (theme, toastType) => {
    switch (toastType) {
        case ToastType.Error:
            return theme.palette.error;
        case ToastType.Warning:
            return theme.palette.warning;
        case ToastType.Success:
            return theme.palette.success;
        default:
            return theme.palette.info;
    }
};
const StyledProgressBar = styled.div `
	grid-area: progress;

	${(props) => `
		margin: 0 ${props.theme.spacing.xs};
		width: calc(100% - ${props.theme.spacing.xs} * 2);
		height: 4px;
		background: ${getThemeColorForToastType(props.theme, props.$type)};
		transition: all ${props.$autoCloseMs}ms linear;
	`}

	&.autoclose {
		justify-self: end;
		width: 0%;
	}
`;
const StyledToast = styled.div `
	display: grid;
	grid-template-columns: auto 1fr auto;
	grid-template-areas:
		'icon text close'
		'progress progress progress';
	align-items: flex-start;

	${(props) => `
		background: ${props.theme.primary};
		color: ${props.theme.secondary};
		border-left: 16px solid ${getThemeColorForToastType(props.theme, props.$type)};
		border-radius: ${props.theme.spacing.xs};
		padding: ${props.theme.spacing.md};
		row-gap: 0;
		column-gap: ${props.theme.spacing.md};
		max-width: 80vw;
		min-width: ${props.theme.sizing.xl};
		transition: all ${props.theme.animationDurationMs}ms ${AnimationTimings.expressive};
	`}

	&.toast-enter,
	&.toast-exit,
	&.toast-exit-done {
		opacity: 0;
		transform: translate3d(calc(100% + 2rem), 0, 0);
	}
	&.toast-enter-active {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
`;
const StyledText = styled.p `
	grid-area: text;
	margin: 0;
	padding: 0;
	text-align: left;
	white-space: pre-wrap;
	overflow-x: auto;
	overflow-wrap: break-word;

	${(props) => `
		padding-bottom: ${props.theme.spacing.md};
	`}
`;
const StyledAction = styled.button `
	border: 0;
	background: unset;
	color: ${(props) => props.theme.secondary};
	text-decoration: underline;
	cursor: pointer;

	:hover {
		color: ${(props) => props.theme.palette.brand1};
	}
`;
const StyledIcon = styled(Icon) `
	grid-area: icon;
	padding: 0;
	${(props) => `
		color: ${getThemeColorForToastType(props.theme, props.$type)};
		padding-bottom: ${props.theme.spacing.md};
	`}
`;
const StyledCloseIcon = styled(Icon) `
	grid-area: close;
	cursor: pointer;
	padding: 0;

	${(props) => `
		padding-bottom: ${props.theme.spacing.md};
	`}
`;
//# sourceMappingURL=Toast.js.map